(ns common.transaction-process.templates.capman.common
  "Shared base for all CapMan transaction process templates.
   Each concrete template (Nest, Real Estate, Other) calls `build-template`
   with its strategy-specific fields to produce a complete template map.
   Field configs use the survey rule language (common.visibility, common.mandatory)
   for visibility and mandatoriness. Label and data-type are auto-resolved from schema."
  (:require
   [common.schema.entities.attestation :as attestation-schema]
   [common.schema.entities.decision :as decision-schema]
   [common.schema.entities.aifm-decision.schema :as decision-aifm-schema]
   [common.schema.entities.decision-condition :as decision-condition-schema]
   [common.schema.entities.ic-decision.schema :as decision-ic-schema]
   [common.schema.entities.decision-participant :as decision-participant-schema]
   [common.schema.entities.declaration :as declaration-schema]
   [common.schema.entities.investment-reservation :as investment-reservation-schema]
   [common.schema.entities.transaction-process :as tp-schema]
   [common.transaction-process.templates.authoring :as a]))

(def all-stages [:preparation :review :approval :signing :closing])
(def all-teams  [:investment-team :platform-team :portfolio-manager])

(def ^:private platform-write-others-read
  {:investment-team   :r
   :platform-team     :w
   :portfolio-manager :r})

(def ^:private schema-lookup
  "Map of field-kw → schema metadata. Used to auto-resolve :field/label,
   :field/data-type, and :field/options for fields used in this template.
   Spans the TP root schema and the sub-entity schemas the templates reference.
   Note on merge order: when the same kw appears in multiple sub-entity schemas
   (e.g. :decision/fund-has-sufficient-capacity in both decision-ic and
   decision-aifm), the later entry wins. Verified that overlapping
   template-relevant kws carry identical labels across decision-ic / decision-aifm."
  (into {} (concat tp-schema/schema
                   decision-schema/schema
                   decision-ic-schema/schema
                   decision-aifm-schema/schema
                   decision-participant-schema/schema
                   decision-condition-schema/schema
                   attestation-schema/schema
                   declaration-schema/schema
                   investment-reservation-schema/schema)))

(defn permission-row
  "Build a single-field permission map across all stages."
  [tier]
  (let [r-all  (zipmap all-teams (repeat :r))
        w-all  (zipmap all-teams (repeat :w))
        none   (zipmap all-teams (repeat :none))]
    (case tier
      :proposal
      {:preparation w-all
       :review      {:investment-team :r :platform-team :w :portfolio-manager :r}
       :approval    r-all
       :signing     r-all
       :closing     r-all}

      :decision
      {:preparation none
       :review      none
       :approval    {:investment-team :w :platform-team :r :portfolio-manager :w}
       :signing     r-all
       :closing     r-all}

      :closing
      {:preparation none
       :review      none
       :approval    none
       :signing     none
       :closing     platform-write-others-read}

      :transaction
      {:preparation none
       :review      none
       :approval    none
       :signing     w-all
       :closing     platform-write-others-read}

      :verification
      {:preparation none
       :review      {:investment-team :r :platform-team :w :portfolio-manager :r}
       :approval    {:investment-team :r :platform-team :w :portfolio-manager :r}
       :signing     r-all
       :closing     r-all}

      :verification-condition-status
      {:preparation none
       :review      {:investment-team :r :platform-team :w :portfolio-manager :r}
       :approval    w-all
       :signing     r-all
       :closing     r-all}

      :declaration
      {:preparation {:investment-team :w :platform-team :w :portfolio-manager :r}
       :review      r-all
       :approval    r-all
       :signing     r-all
       :closing     r-all}

      ;; Read-only everywhere: the computed investment totals are never user-editable.
      :computed
      (zipmap all-stages (repeat r-all)))))

(def ^:private coi-answer-options
  [{:value :coi-answer/yes            :label "Yes"}
   {:value :coi-answer/no             :label "No"}
   {:value :coi-answer/not-applicable
    :label "Not applicable"}])

(def ^:private coi-concerning-answers
  "Answers that require the user to provide an explanatory comment."
  [:coi-answer/yes :coi-answer/not-applicable])

(def ^:private yes-no-keyword-options
  "Boolean fields rendered as a Yes/No dropdown (same override pattern as
   debt-financing? / closing-amount-paid?)."
  [{:value true :label "Yes"} {:value false :label "No"}])

(defn- yes-no-field
  "Field-config for a boolean field shown as a Yes/No dropdown. `overrides` merges
   last (e.g. {:field/full-width true} or a visibility rule)."
  [overrides]
  (merge {:field/visibility    :visibility/always
          :field/mandatoriness :mandatory/optional
          :field/data-type     :data.type/keyword
          :field/options       yes-no-keyword-options}
         overrides))

(defn- visible-when-coi-concerning
  "Visibility rule: comment is visible when the CoI answer is 'yes' or 'not applicable'."
  [field-kw]
  (a/field-visible-when field-kw :operand/any-of coi-concerning-answers))

;; The Preparation proposal fields, split into three field-cards per CapMan's
;; 2026-05-19 feedback (previously all under one "Transaction details" card).
(def ^:private target-entity-detail-fields
  [:transaction-process/short-name
   :transaction-process/target-entities
   :transaction-process/target-entity-business-ids
   :transaction-process/target-asset-property-ids
   :transaction-process/counterparties
   :transaction-process/target-company-hq-country
   :transaction-process/target-company-hq-city])

(def ^:private transaction-detail-fields
  ;; Planned ownership percentage (the stake the fund buys) is the headline
  ;; ownership field; CapMan dropped the legacy Counterparty ownership (2026-06).
  ;; Competition-law control + co-investor questions follow it; the MIP-only
  ;; diluted-ownership pair shows for MIP transactions.
  [:transaction-process/investment-type
   :transaction-process/transaction-purchase-price
   :transaction-process/fx-rate
   :transaction-process/base-case-irr
   :transaction-process/base-case-tmm
   :transaction-process/dividend-yield
   :transaction-process/acquired-ownership
   :transaction-process/competition-law-control?
   :transaction-process/competition-law-control-percentage
   :transaction-process/co-investors?
   :transaction-process/co-investors-details
   :transaction-process/diluted-ownership-current
   :transaction-process/diluted-ownership-after
   :transaction-process/dd-types-conducted])

(def ^:private computed-amount-fields
  "Read-only auto-calculated totals (CapMan 'Maximum = Sum of above',
   'Total = SUM of all'). Backed by `calculated-field`s in the TP schema
   (system-write); they get their own all-:r permission row and are resolved into
   the form's entity-data on the frontend (ui.view.transaction-process.sub)."
  [:transaction-process/capman-maximum-investment-amount
   :transaction-process/capman-total-investment-amount])

(def ^:private investment-amount-fields
  [:transaction-process/equity-amount
   :transaction-process/transaction-costs
   :transaction-process/capital-transfer-tax
   :transaction-process/earnout-amount
   :transaction-process/guarantee-amount
   :transaction-process/guarantee-details
   :transaction-process/currency-buffer
   :transaction-process/capman-maximum-investment-amount
   :transaction-process/debt-financing?
   :transaction-process/debt-amount
   :transaction-process/debt-financing-type
   :transaction-process/debt-financing-secured?
   :transaction-process/capman-total-investment-amount])

(def ^:private proposal-fields
  "Every editable Preparation proposal field across the three field-cards (the
   computed totals are excluded — they carry an all-:r permission row instead).
   Used for the :proposal permission row."
  (vec (remove (set computed-amount-fields)
               (concat target-entity-detail-fields
                       transaction-detail-fields
                       investment-amount-fields))))

;; CapMan 2026-06: Conflict of interest, Tax considerations and Nominated counsel
;; are now three separate Preparation subsections (cards). Tax questions moved out
;; of the CoI card into their own; each group gets a new follow-up question.
(def ^:private coi-fields
  [:transaction-process/coi-other-capman-entity
   :transaction-process/coi-other-capman-entity-comment
   :transaction-process/coi-capman-person-involved
   :transaction-process/coi-capman-person-involved-comment
   :transaction-process/coi-counterparty-linked-to-fund
   :transaction-process/coi-counterparty-linked-to-fund-comment
   :transaction-process/coi-capman-conflicts-handled
   :transaction-process/coi-capman-conflicts-handled-comment])

(def ^:private tax-consideration-fields
  [:transaction-process/coi-tax-material-risk
   :transaction-process/coi-tax-material-risk-comment
   :transaction-process/coi-tax-avoidance-artificial-steps
   :transaction-process/coi-tax-avoidance-artificial-steps-comment
   :transaction-process/tax-cross-border?
   :transaction-process/tax-dac6-non-reportable?])

(def ^:private nominated-counsel-fields
  [:transaction-process/nominated-counsel-company
   :transaction-process/nominated-counsel-person
   :transaction-process/nominated-counsel-reviewed?])

(def ^:private date-fields
  "Anticipated dates are collected through the relevant transition modal (see
  `transitions` below). Kept as a vector so permission/field-resolution code can
  reference them as a group."
  [:transaction-process/anticipated-decision-date
   :transaction-process/anticipated-ic-decision-date
   :transaction-process/anticipated-luxco-decision-date
   :transaction-process/anticipated-closing-date])

(def ^:private closing-fields
  [:transaction-process/closing-amount
   :transaction-process/actual-closing-date
   :transaction-process/closing-amount-paid?
   :transaction-process/closing-amount-paid-date])

(def ^:private sub-entity-decision-fields
  [:decision/date
   :decision/fund-has-sufficient-capacity
   :decision/complies-with-investment-strategy
   :decision/ic-approves-proposed-investment
   :decision/portfolio-manager-approves-proposed-investment
   :decision/board-approval-needed
   :decision/advisory-board-approval-needed
   :decision/conditions
   :decision/participants
   :decision/investment-reservations
   :decision/notes])

(def ^:private reservation-form-layout
  "Layout for the PM-approval decision's Reservations section, as ordered rows.
   Within a row a bare field-kw is one field; a nested vector is a sub-group that
   stays together and wraps as a block. So the amount sits beside the expiry/void
   sub-group when wide; when the row can't fit, the pair drops below the amount
   (both 50/50) rather than splitting around it; when narrower still, the pair
   stacks. Guarantee given-to/by are their own row (50/50, stacking only when very
   narrow) so they never bleed into the amount row. The render layer
   (decision modal sections) turns this into nested flex-wrap rows; per-field
   min-widths (which drive the wrap points) default by data-type there. Per-field
   visibility is further gated by the reservation type's
   :investment-reservation-type/enabled-fields (admin config)."
  [[:investment-reservation/amount
    [:investment-reservation/expiry-date :investment-reservation/void-date]]
   [:investment-reservation/given-to :investment-reservation/given-by]
   [:investment-reservation/expiry-condition]
   [:investment-reservation/notes]])

(def ^:private sub-entity-reservation-fields
  "Flat list of the reservation form fields (derived from the layout), for
   permission + field-config wiring."
  (vec (flatten reservation-form-layout)))

(def ^:private sub-entity-attestation-fields
  [:attestation/outcome
   :attestation/date
   :attestation/comment
   :attestation/draft?
   :attestation/conditions])

(def ^:private sub-entity-declaration-fields
  [:declaration/not-applicable?
   :declaration/completed?
   :declaration/date
   :declaration/comment])

(def ^:private decision-needed-fields
  "Per-decision 'is this decision needed?' fields, set on Reviewing. Each is a
   first-class field."
  [:transaction-process/pm-approval-decision-needed
   :transaction-process/ic-decision-needed
   :transaction-process/advisory-board-decision-needed
   :transaction-process/aifm-decision-needed])

(def investment-type-options
  "~38 investment type options organized by category."
  [{:value :inv-type/equity
    :label "Equity"
    :group "Equity"}
   {:value :inv-type/common-equity
    :label "Common Equity"
    :group "Equity"}
   {:value :inv-type/preferred-equity
    :label "Preferred Equity"
    :group "Equity"}
   {:value :inv-type/listed-stock
    :label "Listed Stock"
    :group "Equity"}
   {:value :inv-type/warrants
    :label "Warrants"
    :group "Equity"}
   {:value :inv-type/residential
    :label "Residential"
    :group "Real Estate"}
   {:value :inv-type/commercial
    :label "Commercial"
    :group "Real Estate"}
   {:value :inv-type/industrial
    :label "Industrial"
    :group "Real Estate"}
   {:value :inv-type/logistics
    :label "Logistics"
    :group "Real Estate"}
   {:value :inv-type/social-re
    :label "Social RE"
    :group "Real Estate"}
   {:value :inv-type/refurb
    :label "Refurb"
    :group "Real Estate"}
   {:value :inv-type/development
    :label "Development"
    :group "Real Estate"}
   {:value :inv-type/turn-key
    :label "Turn-key"
    :group "Real Estate"}
   {:value :inv-type/forward-purchase
    :label "Forward purchase/funding"
    :group "Real Estate"}
   {:value :inv-type/land
    :label "Land"
    :group "Real Estate"}
   {:value :inv-type/forestry-land
    :label "Forestry land"
    :group "Real Estate"}
   {:value :inv-type/forestry-holding
    :label "Forestry holding"
    :group "Real Estate"}
   {:value :inv-type/forestry-management
    :label "Forestry management"
    :group "Real Estate"}
   {:value :inv-type/short-term
    :label "Short-term"
    :group "Cash"}
   {:value :inv-type/money-market
    :label "Money market"
    :group "Cash"}
   {:value :inv-type/money-deposit
    :label "Money deposit"
    :group "Cash"}
   {:value :inv-type/fx-derivatives
    :label "FX derivatives"
    :group "Hedge"}
   {:value :inv-type/ir-derivatives
    :label "IR derivatives"
    :group "Hedge"}
   {:value :inv-type/guarantee
    :label "Guarantee"
    :group "Other"}
   {:value :inv-type/option
    :label "Option"
    :group "Other"}
   {:value :inv-type/co-investment
    :label "Co-investment"
    :group "Other"}
   {:value :inv-type/bridge
    :label "Bridge"
    :group "Other"}
   {:value :inv-type/senior-loan
    :label "Senior loan"
    :group "Loan"}
   {:value :inv-type/sub-loan
    :label "Subordinated loan"
    :group "Loan"}
   {:value :inv-type/convertible-loan
    :label "Convertible loan"
    :group "Loan"}
   {:value :inv-type/mezzanine-loan
    :label "Mezzanine loan"
    :group "Loan"}
   {:value :inv-type/unitranche-loan
    :label "Unitranche loan"
    :group "Loan"}
   {:value :inv-type/distressed-debt
    :label "Distressed debt"
    :group "Loan"}
   {:value :inv-type/notes
    :label "Notes"
    :group "Loan"}
   {:value :inv-type/bonds
    :label "Bonds"
    :group "Loan"}
   {:value :inv-type/debentures
    :label "Debentures"
    :group "Loan"}
   {:value :inv-type/aif
    :label "AIF"
    :group "Fund"}
   {:value :inv-type/ucits
    :label "UCITS (incl. ETFs/REITs)"
    :group "Fund"}])

(def ^:private document-slot-field-configs
  "All document-upload slots, authored directly as attachment-slot fields. Cards
   reference these by id in `:card/fields` (see `prep-doc-slot-ids` etc.)."
  {;; Preparation
   :transaction-process-attachment/structure-chart
   (a/attachment-slot-field "Structure chart" (a/mandatory-when-tx-type [:initial-investment]))
   :transaction-process-attachment/icm
   (a/attachment-slot-field "ICM" (a/mandatory-when-tx-type [:initial-investment]))
   :transaction-process-attachment/sfdr-conformity-checklist
   (a/attachment-slot-field "Sustainability Considerations (SFDR conformity checklist)")
   :transaction-process-attachment/other-documents
   (a/attachment-slot-field "Other documents" {:field/multiple? true})
   ;; Signing
   :transaction-process-attachment/poa        (a/attachment-slot-field "PoA")
   :transaction-process-attachment/spa        (a/attachment-slot-field "SPA")
   :transaction-process-attachment/sha        (a/attachment-slot-field "SHA")
   :transaction-process-attachment/funds-flow (a/attachment-slot-field "Funds Flow")
   :transaction-process-attachment/loan-documents
   (a/attachment-slot-field "Loan documents" {:field/multiple? true})
   :transaction-process-attachment/signing-other-documents
   (a/attachment-slot-field "Other documents" {:field/multiple? true})
   ;; Closing
   :transaction-process-attachment/closing-memo (a/attachment-slot-field "Closing memo")
   :transaction-process-attachment/funds-flow-documents
   (a/attachment-slot-field "Funds flow documents" {:field/multiple? true})
   :transaction-process-attachment/closing-other-documents
   (a/attachment-slot-field "Other documents" {:field/multiple? true})})

(def ^:private signing-doc-slot-ids
  [:transaction-process-attachment/poa
   :transaction-process-attachment/spa
   :transaction-process-attachment/sha
   :transaction-process-attachment/funds-flow
   :transaction-process-attachment/loan-documents
   :transaction-process-attachment/signing-other-documents])

(def ^:private closing-doc-slot-ids
  [:transaction-process-attachment/closing-memo
   :transaction-process-attachment/funds-flow-documents
   :transaction-process-attachment/closing-other-documents])

(def ^:private signing-documents-card
  {:card/type     :field-card
   :card/id       :signing-documents
   :card/label    "Signing documents"
   :card/position :center
   :card/fields   signing-doc-slot-ids})

(def ^:private common-field-configs
  "Field configs shared across all templates. Label and data-type auto-resolved from schema."
  (merge
   ;; Proposal fields
   {:transaction-process/short-name
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/always}

    :transaction-process/transaction-type
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/always}

    :transaction-process/investment-type
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/options       investment-type-options}

    :transaction-process/transaction-purchase-price
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/fx-rate
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/target-entities
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/always
     :field/placeholder   "e.g. Acme Corp Oy"}

    :transaction-process/target-entity-business-ids
    (merge {:field/visibility :visibility/always
            :field/placeholder "e.g. 1234567-8"}
           (a/mandatory-when-tx-type [:initial-investment]))

    :transaction-process/target-asset-property-ids
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/counterparties
    (merge {:field/visibility :visibility/always
            :field/placeholder "e.g. Seller Oy"}
           (a/mandatory-when-tx-type [:initial-investment :mip]))

    :transaction-process/target-company-hq-country
    (merge {:field/visibility :visibility/always}
           (a/mandatory-when-tx-type [:initial-investment]))

    :transaction-process/target-company-hq-city
    (merge {:field/visibility  :visibility/always
            :field/placeholder "e.g. Helsinki"}
           (a/mandatory-when-tx-type [:initial-investment]))

    :transaction-process/acquired-ownership
    ;; CapMan 2026-06: "Acquired ownership" → "Planned ownership percentage", now
    ;; mandatory (it replaces the dropped Counterparty ownership as the headline
    ;; stake). when-tx-type mirrors the old Counterparty rule so waiver/other deals
    ;; aren't blocked by a required-but-irrelevant field.
    (merge {:field/label      "Planned ownership percentage"
            :field/visibility :visibility/always}
           (a/mandatory-when-tx-type [:initial-investment :follow-on :mip]))

    ;; Competition-law control: Yes/No; when Yes, capture the post-transaction
    ;; ownership percentage.
    :transaction-process/competition-law-control?
    (yes-no-field {:field/full-width true})

    :transaction-process/competition-law-control-percentage
    (a/field-visible-when :transaction-process/competition-law-control?)

    ;; Co-investors: Yes/No; when Yes, name them + their ownership percentages.
    :transaction-process/co-investors?
    (yes-no-field {:field/full-width true})

    :transaction-process/co-investors-details
    (merge (a/field-visible-when :transaction-process/co-investors?)
           {:field/full-width true})

    ;; MIP-only diluted ownership (current / after). Shown for MIP transactions.
    :transaction-process/diluted-ownership-current
    (merge (a/field-visible-when :transaction-process/transaction-type :operand/any-of [:mip])
           {:field/mandatoriness :mandatory/optional})

    :transaction-process/diluted-ownership-after
    (merge (a/field-visible-when :transaction-process/transaction-type :operand/any-of [:mip])
           {:field/mandatoriness :mandatory/optional})

    :transaction-process/base-case-irr
    ;; IRR is a return metric that can legitimately exceed 100% (e.g. early-stage /
    ;; high-multiple deals), so exempt it from the 0-100 cap that applies to
    ;; ownership-style "share of a whole" percentages.
    (merge {:field/visibility           :visibility/always
            :field/uncapped-percentage? true}
           (a/mandatory-when-tx-type [:initial-investment :follow-on]))

    :transaction-process/base-case-tmm
    (merge {:field/visibility :visibility/always}
           (a/mandatory-when-tx-type [:initial-investment :follow-on]))

    :transaction-process/dividend-yield
    {:field/label         "Dividend yield (base case, if applicable)"
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/transaction-costs
    (merge {:field/visibility :visibility/always}
           (a/mandatory-when-tx-type [:initial-investment :follow-on]))

    :transaction-process/capital-transfer-tax
    (merge {:field/visibility :visibility/always}
           (a/mandatory-when-tx-type [:initial-investment :follow-on]))

    :transaction-process/dd-types-conducted
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/options       [{:value :dd-type/kyc         :label "KYC"}
                           {:value :dd-type/legal       :label "Legal"}
                           {:value :dd-type/commercial   :label "Commercial"}
                           {:value :dd-type/technical    :label "Technical"}
                           {:value :dd-type/financial    :label "Financial"}
                           {:value :dd-type/insurance    :label "Insurance"}
                           {:value :dd-type/cyber        :label "Cyber"}
                           {:value :dd-type/esg          :label "ESG"}
                           ;; CapMan: add Tax to DD types. Option only — wiring a
                           ;; Tax declaration slot (schema attr :tax-dd exists) is a
                           ;; slot-add, deferred (see triage doc).
                           {:value :dd-type/tax          :label "Tax"}
                           ;; CapMan 2026-06: N/A option for deals where a given DD
                           ;; classification isn't applicable.
                           {:value :dd-type/not-applicable :label "N/A"}]}

    ;; Investment amount breakdown. Maximum (sum of the equity-side components)
    ;; and Total (Maximum + Debt) are read-only computed `calculated-field`s — see
    ;; capman-*-investment-amount in the TP schema. They carry no mandatoriness and
    ;; are resolved into the form's entity-data client-side.
    :transaction-process/equity-amount
    (merge {:field/visibility :visibility/always}
           (a/mandatory-when-tx-type [:initial-investment :follow-on]))

    :transaction-process/guarantee-amount
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/guarantee-details
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/earnout-amount
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/currency-buffer
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    ;; Clarify which amount is funded by the Fund and which includes acquisition
    ;; debt. These labels are CapMan template copy, not generic schema labels.
    :transaction-process/capman-maximum-investment-amount
    {:field/label         "Maximum investment amount by the Fund"
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/capman-total-investment-amount
    {:field/label         "Total investment amount (incl. debt financing)"
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    ;; Debt financing (transaction-level leverage). The schema field is still a
    ;; boolean, but we render it as a Yes/No dropdown (CapMan testing feedback)
    ;; by overriding :field/data-type → :keyword with boolean-valued options.
    :transaction-process/debt-financing?
    {:field/label         "Is debt financing required to complete the acquisition?"
     :field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/data-type     :data.type/keyword
     :field/options       [{:value true  :label "Yes"}
                           {:value false
                            :label "No"}]}

    :transaction-process/debt-financing-type
    ;; Options come from the schema via enrich-field-configs (single source).
    (a/field-visible-when :transaction-process/debt-financing?)

    :transaction-process/debt-amount
    (a/field-visible-when :transaction-process/debt-financing?)

    :transaction-process/debt-financing-secured?
    (merge (a/field-visible-when :transaction-process/debt-financing?)
           {:field/label     "Have binding commitments for the debt financing been obtained?"
            :field/data-type :data.type/keyword
            :field/options   [{:value true  :label "Yes"}
                              {:value false
                               :label "No"}]
            :field/notice    {:notice/text-key :transaction-process/reserve-full-equity-amount
                              :field/visibility :visibility/conditional
                              :field/visibility-rules
                              [{:query-type :query-type/single-item
                                :operator   :operand/false
                                :field      :transaction-process/debt-financing-secured?}]}})}

   ;; Conflict of interest — Yes/No/Not applicable always mandatory; comment visible + mandatory when answer is concerning (yes or NA)
   (let [coi-answer-config {:field/visibility    :visibility/always
                            :field/mandatoriness :mandatory/always
                            :field/full-width    true
                            :field/options       coi-answer-options}
         coi-comment-config (fn [parent-field]
                              (merge (visible-when-coi-concerning parent-field)
                                     {:field/mandatoriness :mandatory/when-visible
                                      :field/full-width    true}))]
     {:transaction-process/coi-other-capman-entity                        coi-answer-config
      :transaction-process/coi-other-capman-entity-comment                (coi-comment-config :transaction-process/coi-other-capman-entity)
      :transaction-process/coi-capman-person-involved                     coi-answer-config
      :transaction-process/coi-capman-person-involved-comment             (coi-comment-config :transaction-process/coi-capman-person-involved)
      :transaction-process/coi-counterparty-linked-to-fund                coi-answer-config
      :transaction-process/coi-counterparty-linked-to-fund-comment        (coi-comment-config :transaction-process/coi-counterparty-linked-to-fund)
      :transaction-process/coi-tax-material-risk                          coi-answer-config
      :transaction-process/coi-tax-material-risk-comment                  (coi-comment-config :transaction-process/coi-tax-material-risk)
      :transaction-process/coi-tax-avoidance-artificial-steps             coi-answer-config
      :transaction-process/coi-tax-avoidance-artificial-steps-comment     (coi-comment-config :transaction-process/coi-tax-avoidance-artificial-steps)
      :transaction-process/coi-capman-conflicts-handled                   coi-answer-config
      :transaction-process/coi-capman-conflicts-handled-comment           (coi-comment-config :transaction-process/coi-capman-conflicts-handled)})

   ;; Tax considerations extras + Nominated counsel confirmations (CapMan 2026-06).
   {:transaction-process/tax-cross-border?
    (yes-no-field {:field/mandatoriness :mandatory/always
                   :field/full-width true})

    :transaction-process/tax-dac6-non-reportable?
    (merge (a/field-visible-when :transaction-process/tax-cross-border?)
           {:field/data-type  :data.type/keyword
            :field/options    yes-no-keyword-options
            :field/full-width true})

    ;; Company/person are ref fields (entity-search). data-type +
    ;; select-entity-types come from the schema via enrich-field-configs; field-row
    ;; renders them as an entity-search picker.
    :transaction-process/nominated-counsel-company
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/full-width    true
     :create-on-fly?      true}

    :transaction-process/nominated-counsel-person
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/full-width    true
     :create-on-fly?      true}

    :transaction-process/nominated-counsel-reviewed?
    (yes-no-field {:field/full-width true})}

   ;; Anticipated dates. Collected only inside transition modals — never rendered
   ;; on a Preparation card. IC decision date is optional; Lux date only visible
   ;; on Lux deals (the TP's :transaction-process/lux-fund?, snapshotted from the
   ;; vehicle at create).
   {:transaction-process/anticipated-decision-date
    (merge {:field/visibility :visibility/always}
           (a/mandatory-when-tx-type [:initial-investment :follow-on :mip]))

    :transaction-process/anticipated-ic-decision-date
    {:field/visibility    :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/anticipated-luxco-decision-date
    (merge (a/field-visible-when :transaction-process/lux-fund?)
           {:field/mandatoriness :mandatory/optional})

    :transaction-process/anticipated-closing-date
    (merge {:field/visibility :visibility/always}
           (a/mandatory-when-tx-type [:initial-investment]))}

   ;; Closing-stage fields. Closing amount + actual closing date are the
   ;; headline close-out fields (required to complete the transaction); the paid
   ;; amount/date are retained as optional supporting detail.
   {:transaction-process/closing-amount
    (merge (a/visible-from-stage :closing)
           {:field/mandatoriness :mandatory/when-visible})

    :transaction-process/actual-closing-date
    (merge (a/visible-from-stage :closing)
           {:field/mandatoriness :mandatory/when-visible})

    ;; Stored as a boolean, but rendered as a Yes/No dropdown (same override as
    ;; :transaction-process/debt-financing? above): :field/data-type → :keyword
    ;; with boolean-valued options.
    :transaction-process/closing-amount-paid?
    (merge (a/visible-from-stage :closing)
           {:field/mandatoriness :mandatory/optional
            :field/data-type     :data.type/keyword
            :field/options       [{:value true  :label "Yes"}
                                  {:value false :label "No"}]})

    :transaction-process/closing-amount-paid-date
    (merge (a/visible-from-stage :closing)
           {:field/mandatoriness :mandatory/optional})}

   ;; Sub-entity fields. Labels auto-resolve from the sub-entity schemas via
   ;; schema-lookup; :field/label appears here only as a deliberate override
   ;; (typically a shorter table-friendly version of the schema's verbose
   ;; question-style label).
   {:decision/date
    {:field/label "Decision date"
     :field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/always}

    :decision/fund-has-sufficient-capacity
    {:field/label "Fund has sufficient capacity"
     :field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision/complies-with-investment-strategy
    {:field/label "Complies with investment strategy"
     :field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision/ic-approves-proposed-investment
    {:field/label "IC approves proposed investment"
     :field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision/portfolio-manager-approves-proposed-investment
    {:field/label "PM approves proposed investment"
     :field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision/board-approval-needed
    {:field/label "Board approval needed"
     :field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision/advisory-board-approval-needed
    {:field/label "Advisory board approval needed"
     :field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision/conditions
    {:field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision/participants
    {:field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision/investment-reservations
    {:field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :decision/notes
    {:field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}}

   ;; Decision sub-sub-entity (participant / condition) field configs. Label +
   ;; data-type auto-resolve from the participant/condition schemas (added to
   ;; schema-lookup). These entries carry the mandatoriness so the modal's
   ;; asterisks and the "save ready" gate (whose tooltip names the missing fields
   ;; from these labels) are fully template-driven, not hardcoded in the frontend.
   {:decision-participant/attendance {:field/category :decisions :field/mandatoriness :mandatory/always}
    :decision-participant/role       {:field/category :decisions :field/mandatoriness :mandatory/always}
    :decision-participant/notes      {:field/category :decisions :field/mandatoriness :mandatory/optional}
    :decision-condition/title        {:field/category :decisions :field/mandatoriness :mandatory/always}
    :decision-condition/description  {:field/category :decisions :field/mandatoriness :mandatory/optional}}

   {:attestation/outcome
    {:field/label "Outcome"
     :field/category :verifications
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/always}

    :attestation/date
    {:field/label "Date"
     :field/category :verifications
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/always}

    :attestation/comment
    {:field/category :verifications
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :attestation/conditions
    {:field/category :verifications
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}}

   ;; `:declaration/date` is mandatory only once the declaration is marked
   ;; Completed (a conditional rule on `:declaration/completed?`), so the modal's
   ;; date asterisk and the save-ready gate both flip on together — driven by this
   ;; config, not frontend code. The status itself (not a single schema field, but
   ;; the not-applicable?/completed? pair) is structurally required by the modal.
   {:declaration/not-applicable?
    {:field/category :declarations
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :declaration/completed?
    {:field/category :declarations
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :declaration/date
    {:field/label "Date"
     :field/category :declarations
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/conditional
     :field/mandatoriness-rules [{:query-type :query-type/single-item
                                  :operator :operand/any-of
                                  :field :declaration/completed?
                                  :value [true]}]}

    :declaration/comment
    {:field/category :declarations
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}}

   ;; Investment-reservation form fields. Label + data-type auto-resolve from the
   ;; investment-reservation schema (added to schema-lookup). Arrangement lives in
   ;; `reservation-form-layout` (exposed as :template/reservation-layout); per-field
   ;; min-widths default by data-type in the render layer. Optional overrides:
   ;; :field/grow (row proportion) and :field/min-width (px).
   {:investment-reservation/amount           {:field/category :decisions :field/mandatoriness :mandatory/always}
    ;; CapMan: Expiry date should be mandatory. `when-visible` (not `always`) so a
    ;; reservation type that hides expiry via its enabled-fields isn't blocked by a
    ;; required-but-hidden field.
    :investment-reservation/expiry-date      {:field/category :decisions :field/mandatoriness :mandatory/when-visible}
    :investment-reservation/void-date        {:field/category :decisions}
    :investment-reservation/given-to         {:field/category :decisions}
    :investment-reservation/given-by         {:field/category :decisions}
    :investment-reservation/expiry-condition {:field/category :decisions}
    :investment-reservation/notes            {:field/category :decisions}}

   ;; Per-decision "is this decision needed?" fields, set on Reviewing. The
   ;; field's `:field/default-value` (when present) is persisted onto the TP at
   ;; creation (see backend create) — so a customer choosing a different default,
   ;; or making PM approval user-editable, is a template edit, not a code change.
   ;; PM approval defaults to Needed; the rest start unset (the user decides on
   ;; Reviewing, and IC is additionally seeded by the transform-write hook when
   ;; its date is set).
   {:transaction-process/pm-approval-decision-needed
    {:field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional
     :field/default-value tp-schema/needed-value}

    :transaction-process/ic-decision-needed
    {:field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/advisory-board-decision-needed
    {:field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}

    :transaction-process/aifm-decision-needed
    {:field/category :decisions
     :field/visibility :visibility/always
     :field/mandatoriness :mandatory/optional}}

   ;; Permission-only field: it gates the *act* of approving the PM approval
   ;; decision and has no stored attribute, so it never appears on a card and
   ;; carries no visibility or mandatoriness. It is here because the Permissions
   ;; modal labels its rows from `:template/fields` — which is what we want, the
   ;; rule shows up in the matrix like any other. `enrich-from-schema` skips
   ;; fields absent from the schema lookup, so the label must be spelled out.
   {:transaction-process/pm-approval-decision-ready
    {:field/category :decisions
     :field/label    "PM approval decision — approve"}}

   ;; Document slots are value-fields too (see `attachment-slot-field`).
   document-slot-field-configs))

(def ^:private slot-writable-permission
  "Grants :w to every team across every stage."
  (zipmap all-stages (repeat (zipmap all-teams (repeat :w)))))

(def ^:private document-slot-permission
  "Document slots are writable by every team before Closing. During Closing,
   only Platform Team can upload documents; the other teams retain read access."
  (assoc slot-writable-permission :closing platform-write-others-read))

(def ^:private pm-approval-ready-permission
  "Approving the PM approval decision belongs to the Portfolio Manager alone
   (CapMan TA, 2026-09-22). Other process teams keep the ordinary `:decision`
   write permission, so they can still prepare the decision and save it as a
   draft; only this extra gate separates preparing from approving.

   `:decision/*` attributes are shared by every decision slot, so the slot points
   at this field through `:slot/approval-permission-field` — a permission row
   alone could never single out PM approval."
  (assoc (permission-row :decision)
         :approval
         {:investment-team   :none
          :platform-team     :none
          :portfolio-manager :w}))

(def ^:private ic-decision-needed-permission
  "The Preparation transition owns the optional IC-decision skip control. Keep
   the normal verification permissions for later stages, but let every process
   team persist the control while the process is in Preparation."
  (assoc (permission-row :verification)
         :preparation
         (zipmap all-teams (repeat :w))))

(def ^:private common-permissions
  (merge
   (zipmap proposal-fields (repeat (permission-row :proposal)))
   (zipmap computed-amount-fields (repeat (permission-row :computed)))
   (zipmap coi-fields (repeat (permission-row :proposal)))
   (zipmap tax-consideration-fields (repeat (permission-row :proposal)))
   (zipmap nominated-counsel-fields (repeat (permission-row :proposal)))
   (zipmap date-fields (repeat (permission-row :proposal)))
   (zipmap closing-fields (repeat (permission-row :closing)))
   (zipmap sub-entity-decision-fields (repeat (permission-row :decision)))
   (zipmap sub-entity-reservation-fields (repeat (permission-row :decision)))
   (zipmap sub-entity-attestation-fields (repeat (permission-row :verification)))
   {:attestation-condition/status (permission-row :verification-condition-status)}
   (zipmap sub-entity-declaration-fields (repeat (permission-row :declaration)))
   (zipmap (keys document-slot-field-configs) (repeat document-slot-permission))
   {:transaction-process/team-assignments-edn slot-writable-permission
    :transaction-process/status               (permission-row :transaction)}
   (zipmap decision-needed-fields (repeat (permission-row :verification)))
   {:transaction-process/ic-decision-needed          ic-decision-needed-permission
    :transaction-process/pm-approval-decision-ready  pm-approval-ready-permission}))

(def summary-fields
  "Sidebar summary, as ordered groups of field-kws. Label, value formatting,
   ref resolution and enum labels all resolve from the tenant schema at render
   time (see the ::summary-groups sub) — entries carry no per-field metadata.
   Grouped per CapMan testing feedback 2026-05-21: 'Could be arranged.
   Additionally, include colors here: if conditional then yellow, if not
   approved/missing, RED!'"
  [{:summary/group "Transaction"
    :summary/fields [:transaction-process/short-name
                     :transaction-process/vehicle
                     :transaction-process/transaction-type
                     :transaction-process/status]}

   {:summary/group "Target"
    :summary/fields [:transaction-process/target-entities
                     :transaction-process/target-entity-business-ids
                     :transaction-process/target-company-hq-country
                     :transaction-process/target-company-hq-city
                     :transaction-process/counterparties
                     :transaction-process/acquired-ownership]}

   {:summary/group "Return metrics"
    :summary/fields [:transaction-process/base-case-irr
                     :transaction-process/base-case-tmm
                     :transaction-process/dividend-yield]}

   {:summary/group "Investment amounts"
    :summary/fields [:transaction-process/equity-amount
                     :transaction-process/transaction-costs
                     :transaction-process/capital-transfer-tax
                     :transaction-process/guarantee-amount
                     :transaction-process/earnout-amount
                     :transaction-process/currency-buffer
                     :transaction-process/capman-maximum-investment-amount]}

   {:summary/group "Debt financing"
    :summary/fields [:transaction-process/debt-financing?
                     :transaction-process/debt-financing-type
                     :transaction-process/debt-amount
                     :transaction-process/debt-financing-secured?
                     :transaction-process/capman-total-investment-amount]}])

(def team-roles
  [{:team-role/id    :investment-team
    :team-role/label "Investment team"}
   {:team-role/id    :platform-team
    :team-role/label "Platform team"}
   {:team-role/id    :portfolio-manager
    :team-role/label "Portfolio manager"}])

(def transaction-types
  [{:transaction-type/id :initial-investment
    :transaction-type/label "Initial investment"}
   {:transaction-type/id :follow-on
    :transaction-type/label "Follow-on"}
   {:transaction-type/id :mip
    :transaction-type/label "MIP"}
   {:transaction-type/id :other
    :transaction-type/label "Other (waivers etc.)"}])

(def transitions
  [{:transition/from             :preparation
    :transition/to               :review
    :transition/forward-label    "Send for review"
    :transition/confirmation     "I confirm proposal is ready to be reviewed"
    :transition/required-fields  [:transaction-process/short-name]
    :transition/fields           [:transaction-process/anticipated-decision-date
                                  :transaction-process/anticipated-ic-decision-date
                                  :transaction-process/anticipated-luxco-decision-date]
    :transition/skip-slot-controls [{:slot-kw   :transaction-process/ic-decision
                                     :label-key :transaction-process/ic-decision-not-needed}]
    :transition/field-validations {:transaction-process/anticipated-decision-date       {:min-business-days-from-today 3}
                                   :transaction-process/anticipated-ic-decision-date    {:min-business-days-from-today 3}
                                   :transaction-process/anticipated-luxco-decision-date {:min-business-days-from-today 3}}}
   {:transition/from             :review
    :transition/to               :approval
    :transition/forward-label    "Move forward"
    :transition/confirmation     "I confirm proposal is ready to be approved"
    :transition/backward-label   "Send back to previous stage"
    :transition/backward-target  :preparation}
   {:transition/from             :approval
    :transition/to               :signing
    :transition/forward-label    "Move forward"
    :transition/confirmation     "I confirm proposal is ready to be signed"
    :transition/backward-label   "Send back to previous stage"
    :transition/backward-target  :review}
   {:transition/from             :signing
    :transition/to               :closing
    :transition/forward-label    "Move forward"
    :transition/confirmation     "I confirm proposal is ready to close"
    :transition/backward-label   "Send back to previous stage"
    :transition/backward-target  :approval}
   {:transition/from             :closing
    :transition/close-label      "Complete transaction"
    :transition/close-confirmation "I confirm the closing amount has been paid and this transaction is complete."
    :transition/close-action?    true
    :transition/reject-action?   true
    :transition/required-fields  [:transaction-process/closing-amount
                                  :transaction-process/actual-closing-date]
    :transition/field-validations
    {:transaction-process/closing-amount
     {:less-than-or-equal-to-field
      :transaction-process/capman-maximum-investment-amount}}
    :transition/backward-label   "Send back to previous stage"
    :transition/backward-target  :signing}])

(def notifications
  [{:rule-id           :notify-creation
    :notification-type :creation
    :recipients        #{:investment-team :platform-team :portfolio-manager}
    :subject           "{{vehicle-name}}: New transaction - {{process-name}}"
    :body              "A new transaction process has been created by {{created-by}}.\n\nName: {{process-name}}\nVehicle: {{vehicle-name}}\nDate: {{date}}"}

   {:rule-id           :notify-prep-to-review
    :notification-type :stage-transition
    :from-stage        :preparation
    :to-stage          :review
    :recipients        #{:investment-team :platform-team :portfolio-manager}
    :subject           "{{vehicle-name}}: {{process-name}} moved to {{new-stage}}"
    :body              "{{process-name}} has been moved from {{old-stage}} to {{new-stage}} by {{moved-by}}.\n\nVehicle: {{vehicle-name}}\nDate: {{date}}"}

   {:rule-id           :notify-review-to-approval
    :notification-type :stage-transition
    :from-stage        :review
    :to-stage          :approval
    :recipients        #{:portfolio-manager}
    :subject           "{{vehicle-name}}: {{process-name}} moved to {{new-stage}}"
    :body              "{{process-name}} has been moved from {{old-stage}} to {{new-stage}} by {{moved-by}}.\n\nVehicle: {{vehicle-name}}\nDate: {{date}}"}

   {:rule-id           :notify-approval-to-signing
    :notification-type :stage-transition
    :from-stage        :approval
    :to-stage          :signing
    :recipients        #{:investment-team :platform-team}
    :subject           "{{vehicle-name}}: {{process-name}} moved to {{new-stage}}"
    :body              "{{process-name}} has been moved from {{old-stage}} to {{new-stage}} by {{moved-by}}.\n\nVehicle: {{vehicle-name}}\nDate: {{date}}"}

   {:rule-id           :notify-signing-to-closing
    :notification-type :stage-transition
    :from-stage        :signing
    :to-stage          :closing
    :recipients        #{:investment-team :platform-team :portfolio-manager}
    :subject           "{{vehicle-name}}: {{process-name}} moved to {{new-stage}}"
    :body              "{{process-name}} has been moved from {{old-stage}} to {{new-stage}} by {{moved-by}}.\n\nVehicle: {{vehicle-name}}\nDate: {{date}}"}

   {:rule-id           :notify-backward-to-prep
    :notification-type :stage-transition
    :direction         :backward
    :to-stage          :preparation
    :recipients        #{:investment-team :platform-team :portfolio-manager}
    :subject           "{{vehicle-name}}: {{process-name}} sent back to {{new-stage}}"
    :body              "{{process-name}} has been sent back from {{old-stage}} to {{new-stage}} by {{moved-by}}.\n\nVehicle: {{vehicle-name}}\nDate: {{date}}"}

   {:rule-id           :notify-backward-to-approval
    :notification-type :stage-transition
    :direction         :backward
    :to-stage          :approval
    :recipients        #{:portfolio-manager}
    :subject           "{{vehicle-name}}: {{process-name}} sent back to {{new-stage}}"
    :body              "{{process-name}} has been sent back from {{old-stage}} to {{new-stage}} by {{moved-by}}.\n\nVehicle: {{vehicle-name}}\nDate: {{date}}"}

   {:rule-id           :notify-backward-to-signing
    :notification-type :stage-transition
    :direction         :backward
    :to-stage          :signing
    :recipients        #{:portfolio-manager :investment-team}
    :subject           "{{vehicle-name}}: {{process-name}} sent back to {{new-stage}}"
    :body              "{{process-name}} has been sent back from {{old-stage}} to {{new-stage}} by {{moved-by}}.\n\nVehicle: {{vehicle-name}}\nDate: {{date}}"}])

(def stage-gates
  "Maps `to-stage` → set of sub-entity categories whose completion blocks
  forward transitions. Iterated by the transition validator instead of being
  hardcoded in the frontend. Each entry says 'when moving into <stage>, these
  categories must be complete'."
  {:review   #{:declarations}
   :approval #{:attestations :declarations}
   :signing  #{:attestations :declarations :decisions}
   :closing  #{:attestations :declarations :decisions}})

;; Per-type outcome option sets. `:variant` is the status-badge colour the read
;; views render for each value — carried on the option so the frontend never
;; hardcodes a per-keyword variant map.
(def ^:private yes-no-outcome-options
  [{:value :attestation.value/yes
    :label "Yes"
    :variant :success}
   {:value :attestation.value/no
    :label "No"
    :variant :neutral}])

(def ^:private received-outcome-options
  [{:value :attestation.value/received
    :label "Received"
    :variant :success}
   {:value :attestation.value/not-received
    :label "Not received"
    :variant :danger}])

(def ^:private lux-approval-outcome-options
  [{:value :attestation.value/lux-approved
    :label "Approved"
    :variant :success}
   {:value :attestation.value/lux-not-approved
    :label "Not approved"
    :variant :danger}
   {:value :attestation.value/lux-conditionally-approved
    :label "Conditionally approved"
    :variant :warning}])

(def ^:private coi-outcome-options
  [{:value :attestation.value/coi-none
    :label "No identified CoI"
    :variant :success}
   {:value :attestation.value/coi-identified
    :label "Identified CoI"
    :variant :warning}
   {:value :attestation.value/coi-not-applicable
    :label "Not applicable"
    :variant :neutral}
   {:value :attestation.value/coi-identified-red-flag
    :label "Identified CoI - Red flag"
    :variant :danger}])

(def ^:private capacity-outcome-options
  (conj attestation-schema/default-outcome-options
        {:value :attestation.value/external-funding-not-secured
         :label "External funding not secured"
         :variant :danger}))

(defn- outcome-option?
  [outcome-options value]
  (boolean (some #(= value (:value %)) outcome-options)))

;; Layout layer (presentation). References the dictionary entity-slots by key
;; and arranges them: which slots a card shows, in what order, and (for the
;; Verifications card) under which group header. The dictionary carries the slot
;; semantics; arrangement lives only here.

(def ^:private verification-group-layout
  "Ordered Verifications-card groups, each naming the attestation slot-keys it
   renders (in order). A group with `:group/id nil` renders last under no header."
  [{:group/id :legal-checks
    :group/label "Legal checks"
    :group/slots [:transaction-process/coi-assessment
                  :transaction-process/nominated-counsel-confirmation
                  :transaction-process/advisory-board-approval-needed
                  :transaction-process/aifm-board-approval-needed]}

   {:group/id :risk-checks
    :group/label "Risk check"
    :group/slots [:transaction-process/investment-restrictions
                  :transaction-process/risk-mitigants-assessed
                  :transaction-process/dd-conducted]}

   {:group/id :investment-capacity
    :group/label "Investment Capacity"
    :group/slots [:transaction-process/capacity-calculation]}

   {:group/id :sustainability-check
    :group/label "Sustainability check"
    :group/slots [:transaction-process/sustainability-considerations]}

   {:group/id :kyc-check
    :group/label "Know your customer"
    :group/slots [:transaction-process/kyc-check]}

   {:group/id :lux-date-confirmation
    :group/label "Lux date confirmation"
    :group/slots [:transaction-process/luxco-date-approval]}

   {:group/id :tax-check
    :group/label "Tax check"
    :group/slots [:transaction-process/tax-considerations]}])

(defn- verification-cards
  "CapMan 2026-06: render each verification group as its own card (the group label
   becomes the card title, no subtitle) instead of one large 'Verifications' card
   with sub-headers. Each card carries a flat `:card/slots`; the renderer
   self-hides any card whose slots all resolve away (e.g. the Lux card on a
   non-Lux fund)."
  []
  (mapv (fn [{:group/keys [id label slots]}]
          {:card/type     :verifications
           :card/id       id
           :card/label    label
           :card/slots    slots
           :card/position :center})
        verification-group-layout))

(def ^:private all-verification-slot-ids
  (->> verification-group-layout
       (mapcat :group/slots)
       vec))

(def ^:private decision-slot-layout
  [:transaction-process/pm-approval-decision
   :transaction-process/ic-decision
   :transaction-process/advisory-board-decision
   :transaction-process/aifm-decision])

(def ^:private closing-condition-slot-layout
  (into all-verification-slot-ids decision-slot-layout))

(def ^:private declaration-slot-layout
  [:transaction-process/kyc-dd
   :transaction-process/legal-dd
   :transaction-process/commercial-dd
   :transaction-process/technical-dd
   :transaction-process/financial-dd
   :transaction-process/insurance-dd
   :transaction-process/cyber-dd
   :transaction-process/esg-dd])

(def ^:private decision-section-layout
  "Per-decision-slot ordered modal sections. `:details`/`:notes` render the
   decision's scalar fields; `:participants`/`:conditions`/`:reservations` render
   the nested entity-lists the slot carries."
  {:transaction-process/pm-approval-decision    [:details :reservations :conditions :notes]
   :transaction-process/ic-decision             [:details :participants :conditions :notes]
   :transaction-process/advisory-board-decision [:details :participants :conditions :notes]
   :transaction-process/aifm-decision           [:details :participants :conditions :notes]})

(defn- prep-doc-slot-ids
  "The prep Documents card's ordered attachment-slot field-kws. Strategy templates
  can inject extra slot-ids (their field-configs supplied via strategy-field-configs);
  they sit before the catch-all `:other-documents` so it stays last."
  [extra-prep-doc-slot-ids]
  (vec
   (concat [:transaction-process-attachment/structure-chart
            :transaction-process-attachment/icm
            :transaction-process-attachment/sfdr-conformity-checklist]
           extra-prep-doc-slot-ids
           [:transaction-process-attachment/other-documents])))

(defn- make-prep-cards
  [{:keys [strategy-fields strategy-card-label strategy-card-subtitle
           extra-prep-doc-slot-ids extra-investment-amount-fields]}]
  (cond-> [{:card/type     :field-card
            :card/id       :target-entity-details
            :card/label    "Target entity details"
            :card/subtitle "Identify the target entity and counterparties"
            :card/fields   target-entity-detail-fields}
           {:card/type     :field-card
            :card/id       :transaction-details
            :card/label    "Transaction details"
            :card/subtitle "Configure the attributes of the transaction"
            :card/fields   transaction-detail-fields}
           {:card/type     :declarations
            :card/id       :declarations
            :card/label    "Diligence declarations"
            :card/subtitle "Track due diligence completion status"
            :card/slots    declaration-slot-layout}
           {:card/type     :field-card
            :card/id       :investment-amounts
            :card/label    "Investment amounts"
            :card/subtitle "Break down the investment. Maximum and total are calculated automatically."
            ;; Strategy-specific amount fields (e.g. Nest's Loan / Principal) lead
            ;; the breakdown; they feed the computed Maximum/Total like the shared
            ;; components.
            :card/fields   (into (vec extra-investment-amount-fields) investment-amount-fields)}]
    (seq strategy-fields)
    (conj {:card/type     :field-card
           :card/id       :strategy-fields
           :card/label    (or strategy-card-label "Strategy-specific fields")
           :card/subtitle (or strategy-card-subtitle "Fields specific to the investment strategy")
           :card/fields   strategy-fields})
    true
    (into [{:card/type     :field-card
            :card/id       :conflict-of-interest
            :card/label    "Conflict of interest"
            :card/subtitle "Disclose any conflicts of interest"
            :card/fields   coi-fields}
           {:card/type     :field-card
            :card/id       :tax-considerations
            :card/label    "Tax considerations"
            :card/subtitle "Assess the tax position of the transaction"
            :card/fields   tax-consideration-fields}
           {:card/type     :field-card
            :card/id       :nominated-counsel
            :card/label    "Nominated counsel confirmations"
            :card/subtitle "Identify nominated counsel and confirm their review"
            :card/fields   nominated-counsel-fields}
           {:card/type   :field-card
            :card/id     :preparation-documents
            :card/label  "Documents"
            :card/fields (prep-doc-slot-ids extra-prep-doc-slot-ids)}
           {:card/type     :transaction-teams
            :card/id       :teams
            :card/label    "Transaction teams"
            :card/subtitle "See who's involved in this transaction"}])))

(defn- make-stages
  "Stages carry one interpreter-level flag:
  - `:stage/decision-config?` — this is the stage where the user toggles which
    decisions are required. Drives the requiredness-row UI in the decisions card."
  [strategy-opts]
  [{:stage/id     :preparation
    :stage/label  "Preparation"
    :stage/layout :full-width
    :stage/cards  (make-prep-cards strategy-opts)}

   {:stage/id                :review
    :stage/label             "Reviewing"
    :stage/layout            :three-column
    :stage/decision-config?  true
    :stage/cards
    (into [{:card/type     :transaction-summary
            :card/id       :summary
            :card/position :left}
           {:card/type     :decisions
            :card/id       :decisions
            :card/label    "Decisions needed"
            :card/subtitle "Select which decisions are required for this transaction. Some are mandatory and cannot be changed."
            :card/slots    decision-slot-layout
            :card/position :center}]
          (verification-cards))}

   {:stage/id     :approval
    :stage/label  "Approving"
    :stage/layout :three-column
    :stage/cards
    (into [{:card/type     :transaction-summary
            :card/id       :summary
            :card/position :left}
           {:card/type     :decisions
            :card/id       :decisions
            :card/label    "Decisions"
            :card/subtitle "Log each required decision. Non-required decisions are shown for reference."
            :card/slots    decision-slot-layout
            :card/position :center}]
          (verification-cards))}

   {:stage/id     :signing
    :stage/label  "Signing"
    :stage/layout :three-column
    :stage/cards
    [{:card/type     :transaction-summary
      :card/id       :summary
      :card/position :left}
     {:card/type     :verifications
      :card/id       :signing-verifications
      :card/label    "Verifications"
      :card/subtitle "Sign-off entries required before this transaction can move to closing."
      :card/position :center
      :card/slots    [:transaction-process/luxco-date-approval]}
     signing-documents-card]}

   {:stage/id     :closing
    :stage/label  "Closing"
    :stage/layout :three-column
    :stage/cards
    [{:card/type     :transaction-summary
      :card/id       :summary
      :card/position :left}
     {:card/type     :field-card
      :card/id       :closing-payment
      :card/label    "Closing"
      :card/subtitle "Confirm the actual closing date and amount."
      :card/fields   [:transaction-process/closing-amount
                      :transaction-process/actual-closing-date
                      :transaction-process/closing-amount-paid?
                      :transaction-process/closing-amount-paid-date]
      :card/position :center}
     signing-documents-card
     {:card/type     :field-card
      :card/id       :closing-documents
      :card/label    "Closing documents"
      :card/position :center
      :card/fields   closing-doc-slot-ids}
     {:card/type     :conditions
      :card/id       :closing-conditions
      :card/label    "Conditions"
      :card/subtitle "Review conditions before completing this transaction."
      :card/position :center
      :card/slots    closing-condition-slot-layout}]}])

;; Field dictionary (the semantic layer). Each single sub-entity is a named
;; entity-slot whose per-slot config (outcome options, requiredness, visibility,
;; nested fields) lives here beside the scalar fields. The slots are
;; hand-authored explicit data (helpers below assemble the repetitive structure;
;; the per-slot data is written out per slot). Leaf field configs
;; (mandatoriness/visibility/label) are pulled from `common-field-configs`, so the
;; modal asterisks and save-ready gate stay template-driven. Presentation
;; (stages/cards/sections/grouping/reservation layout) is a SEPARATE layout
;; layer. See `dictionary.cljc`.

(def ^:private sub-entity-namespaces
  "Field-kw namespaces that move OFF the top-level dictionary and INTO a slot's
   nested template (they are sub-entity fields, not transaction-process fields)."
  #{"decision" "attestation" "declaration"
    "investment-reservation" "decision-participant" "decision-condition"})

(defn- top-level-entries
  "The dictionary's top-level value-field + attachment-slot entries: every
   `field-configs` entry except the sub-entity fields (which nest under slots).
   Attachment-slot configs are tagged with `:field/kind` so the resolver
   dispatches on kind rather than re-sniffing `:data.type/attachment-slot`."
  [field-configs]
  (->> field-configs
       (remove (fn [[k _]] (contains? sub-entity-namespaces (namespace k))))
       (map (fn [[k cfg]]
              [k (cond-> cfg
                   (= :data.type/attachment-slot (:field/data-type cfg))
                   (assoc :field/kind :field.kind/attachment-slot))]))
       (into {})))

(defn- leaves
  "Nested value-field template: each field-kw mapped to its config from
   `field-configs` (label/data-type/mandatoriness already enriched)."
  [field-configs field-kws]
  (into {} (map (juxt identity #(get field-configs %)) field-kws)))

(defn- decision-participants-list [field-configs]
  {:field/kind       :field.kind/entity-list
   :slot/entity-type :entity.type/decision-participant
   :item-template    (leaves field-configs [:decision-participant/attendance
                                            :decision-participant/role
                                            :decision-participant/notes])})

(defn- decision-conditions-list [field-configs]
  {:field/kind       :field.kind/entity-list
   :slot/entity-type :entity.type/decision-condition
   :item-template    (leaves field-configs [:decision-condition/title
                                            :decision-condition/description])})

(defn- decision-reservations-list [field-configs]
  {:field/kind       :field.kind/entity-list
   :slot/entity-type :entity.type/investment-reservation
   :item-template    (leaves field-configs sub-entity-reservation-fields)})

(defn- decision-slot
  "An entity-slot for a decision. Requiredness reads the decision's first-class
   per-process `:needed-field` (tri-state; `:editable?` mirrors whether the
   Reviewing card lets the user change it). `:children` lists which sub-entity
   lists this decision carries (PM keeps reservations + conditions and drops
   participants; IC/advisory/AIFM keep participants + conditions and drop
   reservations). Date + notes are always present."
  [field-configs {:keys [label entity-type needed-field editable? required-by-default?
                         hide-when-not-needed? children hooks block-on-danger-attestation?
                         approval-permission-field]}]
  (let [children   (set children)
        child-lists (cond-> {}
                      (children :participants) (assoc :decision/participants (decision-participants-list field-configs))
                      (children :conditions)   (assoc :decision/conditions (decision-conditions-list field-configs))
                      (children :reservations) (assoc :decision/investment-reservations (decision-reservations-list field-configs)))]
    (cond-> {:field/kind       :field.kind/entity-slot
             :slot/entity-type entity-type
             :slot/label       label
             :slot/required    {:from-field  needed-field
                                :true-value  tp-schema/needed-value
                                :false-value tp-schema/not-needed-value
                                :default?    required-by-default?
                                :editable?   (boolean editable?)}
             :slot/template    (merge (leaves field-configs [:decision/date :decision/notes])
                                      child-lists)}
      (seq hooks) (assoc :slot/hooks hooks)
      hide-when-not-needed?
      (assoc :slot/hide-when-not-needed? true)
      block-on-danger-attestation?
      (assoc :slot/block-on-danger-attestation? true)
      approval-permission-field
      (assoc :slot/approval-permission-field approval-permission-field))))

(defn- attestation-slot
  "An entity-slot for an attestation (all share `:entity.type/attestation`). The
   outcome's per-slot option set is the key bit of per-slot config; `:editable?`
   controls whether the slot's requiredness can be toggled. A Lux-only
   attestation carries a visibility rule and hides its date field."
  [field-configs {:keys [label required? editable? outcome-options hide-date? visibility-rules]}]
  (let [prefill  (cond-> {}
                   (not hide-date?) (assoc :attestation/date :tp.prefill/today)
                   (outcome-option? outcome-options :attestation.value/no-objections)
                   (assoc :attestation/outcome :attestation.value/no-objections))
        template (cond-> {:attestation/outcome (assoc (get field-configs :attestation/outcome)
                                                      :field/options outcome-options)
                          :attestation/comment (get field-configs :attestation/comment)}
                   (not hide-date?) (assoc :attestation/date (get field-configs :attestation/date)))]
    (cond-> {:field/kind       :field.kind/entity-slot
             :slot/entity-type :entity.type/attestation
             :slot/label       label
             :slot/required    {:default? (boolean required?) :editable? (boolean editable?)}
             :slot/template    template}
      ;; Default the verification date to today when shown, and default the outcome
      ;; only for slots whose option set includes the standard approved value.
      ;; `:tp.prefill/today` is resolved at form-open time.
      (seq prefill) (assoc :slot/prefill prefill)
      (seq visibility-rules) (assoc :slot/visibility       :visibility/conditional
                                    :slot/visibility-rules (vec visibility-rules)))))

(defn- declaration-slot
  "An entity-slot for a declaration (all share `:entity.type/declaration`). A
   DD-linked declaration is visible only when its DD-type is selected in
   `:transaction-process/dd-types-conducted`. Deselecting the type removes an
   existing declaration from scope; the backend unlinks and soft-disables it."
  [field-configs {:keys [label required? dd-type-id]}]
  (cond-> {:field/kind       :field.kind/entity-slot
           :slot/entity-type :entity.type/declaration
           :slot/label       label
           :slot/required    {:default? (boolean required?)}
           :slot/template    (leaves field-configs [:declaration/not-applicable?
                                                    :declaration/completed?
                                                    :declaration/date
                                                    :declaration/comment])}
    dd-type-id (assoc :slot/visibility          :visibility/conditional
                      :slot/visibility-rules    [{:query-type :query-type/single-item
                                                  :operator   :operand/any-of
                                                  :field      :transaction-process/dd-types-conducted
                                                  :value      [dd-type-id]}]
                      :slot/retain-when-hidden? false)))

(def ^:private lux-fund-visibility-rules
  [{:query-type :query-type/single-item
    :operator   :operand/true
    :field      :transaction-process/lux-fund?}])

(def ^:private decision-slot-specs
  [[:transaction-process/pm-approval-decision
    {:label "PM approval"
     :entity-type :entity.type/pm-approval-decision
     :needed-field :transaction-process/pm-approval-decision-needed
     :block-on-danger-attestation? true
     :editable? false
     :children [:conditions :reservations]
     :approval-permission-field :transaction-process/pm-approval-decision-ready
     :hooks {:hook/prefill :capman.tp/reservation-prefill}}]

   [:transaction-process/ic-decision
    {:label "IC decision"
     :entity-type :entity.type/ic-decision
     :needed-field :transaction-process/ic-decision-needed
     :required-by-default? true
     :hide-when-not-needed? true
     :editable? true
     :children [:participants :conditions]}]

   [:transaction-process/advisory-board-decision
    {:label "Advisory board decision"
     :entity-type :entity.type/advisory-board-decision
     :needed-field :transaction-process/advisory-board-decision-needed
     :editable? true
     :children [:participants :conditions]}]

   [:transaction-process/aifm-decision
    {:label "AIFM Board decision"
     :entity-type :entity.type/aifm-decision
     :needed-field :transaction-process/aifm-decision-needed
     :editable? true
     :children [:participants :conditions]}]])

(def ^:private attestation-slot-specs
  [[:transaction-process/coi-assessment
    {:label "CoI assessment"
     :required? true
     :editable? false
     :outcome-options coi-outcome-options}]

   [:transaction-process/nominated-counsel-confirmation
    {:label "Nominated counsel confirmation"
     :required? true
     :editable? false
     :outcome-options received-outcome-options}]

   [:transaction-process/advisory-board-approval-needed
    {:label "Advisory Board approval"
     :required? false
     :editable? false
     :outcome-options yes-no-outcome-options}]

   [:transaction-process/aifm-board-approval-needed
    {:label "AIFM board approval"
     :required? false
     :editable? false
     :outcome-options yes-no-outcome-options}]

   [:transaction-process/investment-restrictions
    {:label "Investment restrictions"
     :required? true
     :editable? true
     :outcome-options attestation-schema/default-outcome-options}]

   [:transaction-process/risk-mitigants-assessed
    {:label "Risk & mitigants assessed"
     :required? true
     :editable? true
     :outcome-options attestation-schema/default-outcome-options}]

   [:transaction-process/dd-conducted
    {:label "DD conducted"
     :required? true
     :editable? true
     :outcome-options attestation-schema/default-outcome-options}]

   [:transaction-process/capacity-calculation
    {:label "Capacity calculation"
     :required? true
     :editable? true
     :outcome-options capacity-outcome-options}]

   [:transaction-process/sustainability-considerations
    {:label "Sustainability considerations"
     :required? true
     :editable? true
     :outcome-options attestation-schema/default-outcome-options}]

   [:transaction-process/kyc-check
    {:label "KYC check"
     :required? true
     :editable? true
     :outcome-options attestation-schema/default-outcome-options}]

   [:transaction-process/luxco-date-approval
    {:label "Lux date approval"
     :required? true
     :editable? false
     :outcome-options lux-approval-outcome-options
     :hide-date? true
     :visibility-rules lux-fund-visibility-rules}]

   [:transaction-process/tax-considerations
    {:label "Tax considerations"
     :required? true
     :editable? true
     :outcome-options attestation-schema/default-outcome-options}]])

(def ^:private declaration-slot-specs
  [[:transaction-process/kyc-dd
    {:label "KYC diligence complete"
     :required? true
     :dd-type-id :dd-type/kyc}]

   [:transaction-process/legal-dd
    {:label "Legal DD complete"
     :required? true
     :dd-type-id :dd-type/legal}]

   [:transaction-process/commercial-dd
    {:label "Commercial DD complete"
     :required? true
     :dd-type-id :dd-type/commercial}]

   [:transaction-process/technical-dd
    {:label "Technical DD complete"
     :required? true
     :dd-type-id :dd-type/technical}]

   [:transaction-process/financial-dd
    {:label "Financial DD complete"
     :required? true
     :dd-type-id :dd-type/financial}]

   [:transaction-process/insurance-dd
    {:label "Insurance DD complete"
     :required? true
     :dd-type-id :dd-type/insurance}]

   [:transaction-process/cyber-dd
    {:label "Cyber DD complete"
     :required? true
     :dd-type-id :dd-type/cyber}]

   [:transaction-process/esg-dd
    {:label "ESG analysis complete"
     :required? true
     :dd-type-id :dd-type/esg}]])

(def ^:private attestation-context-fields
  {:transaction-process/capacity-calculation
   [:transaction-process/capman-maximum-investment-amount
    :transaction-process/capman-total-investment-amount]})

(def ^:private attestation-detail-fields
  {:transaction-process/coi-assessment coi-fields
   :transaction-process/tax-considerations tax-consideration-fields})

(defn build-dictionary
  "Assemble the field dictionary from the enriched flat `field-configs` (the
   scalar/attachment value-fields) plus the hand-authored entity-slot specs. The
   result is the recursive map validated by `common.transaction-process.dictionary`."
  [field-configs]
  (let [slot (fn [build specs]
               (into {} (map (fn [[k spec]] [k (build field-configs spec)]) specs)))]
    (merge (top-level-entries field-configs)
           (slot decision-slot    decision-slot-specs)
           (slot attestation-slot attestation-slot-specs)
           (slot declaration-slot declaration-slot-specs))))

(defn build-template
  "Assemble a complete template from shared base + strategy-specific fields.
   Field label and data-type are auto-resolved from the schema."
  [{:keys [template-id template-name
           strategy-fields strategy-field-configs
           strategy-card-label strategy-card-subtitle
           extra-prep-doc-slot-ids extra-investment-amount-fields]}]
  (let [field-configs (-> (merge common-field-configs strategy-field-configs)
                          (assoc-in [:transaction-process/transaction-type :field/options]
                                    (a/transaction-type-options transaction-types))
                          (a/enrich-field-configs schema-lookup))]
    {:template/id   template-id
     :template/name template-name

     :template/summary-fields summary-fields
     :template/transaction-types transaction-types
     :template/team-roles team-roles

     :template/stages
     (make-stages {:strategy-fields                strategy-fields
                   :strategy-card-label            strategy-card-label
                   :strategy-card-subtitle         strategy-card-subtitle
                   :extra-prep-doc-slot-ids        extra-prep-doc-slot-ids
                   :extra-investment-amount-fields extra-investment-amount-fields})

     :template/transitions transitions

     :template/permissions
     (merge common-permissions
            (zipmap (concat strategy-fields extra-investment-amount-fields)
                    (repeat (permission-row :proposal))))

     :template/fields field-configs

     ;; Semantic layer: the recursive field dictionary (the named entity-slots
     ;; with nested templates, beside the scalar value-fields). The presentation
     ;; arrangement lives in the layout keys below (decision-sections, the
     ;; Verifications group layout).
     :template/dictionary (build-dictionary field-configs)

     :template/stage-gates        stage-gates
     :template/decision-sections decision-section-layout
     :template/reservation-layout reservation-form-layout
     :template/attestation-context-fields attestation-context-fields
     :template/attestation-detail-fields attestation-detail-fields
     :template/notifications notifications

     ;; Entity-level lifecycle hooks (run by keyword; see common.transaction-process.hooks).
     ;; transform-write hooks rewrite a pending TP update map before it commits.
     :template/hooks {:hook/transform-write [:capman.tp/seed-ic-decision-needed]}

     :template/stage-transition-notification-notice
     "You are about to move this transaction to the next stage. The following people will be notified:"}))
